import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  Provider,
  Type,
  ViewChild,
  forwardRef,
  inject
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  NG_ASYNC_VALIDATORS,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';

import { filter, startWith } from 'rxjs';

import { sanitizeText } from '@hdms/shared/util-core';

@Directive()
export abstract class CustomInputComponentAbstract<TValue = string | object> implements ControlValueAccessor, OnInit {
  private static increment = 0;

  private readonly validators = inject<ValidatorFn[]>(NG_VALIDATORS, { optional: true });
  private readonly asyncValidators = inject<AsyncValidatorFn[]>(NG_ASYNC_VALIDATORS, { optional: true });
  private readonly controlContainer = inject(ControlContainer, { optional: true, host: true });
  protected readonly cdr = inject(ChangeDetectorRef);

  protected instanceId: number;
  protected internalValue: TValue = null;
  protected internalDisabled = false;
  protected isNgForm: boolean;
  protected formControl: UntypedFormControl;
  protected isReadonly = false;

  @ViewChild('inputRef', {
    static: true,
    read: ElementRef
  })
  inputElementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>;

  @Input() name: string;
  @Input() inputId: string;
  @Input() formControlName: string;
  @Input() validatorMessageContext: string;
  @Input() validationMessage: object;
  @Input() validateOnBlur: boolean;

  @Input()
  set readonly(status: boolean) {
    this.isReadonly = status;
    this.cdr.markForCheck();
  }

  get readonly(): boolean {
    return this.isReadonly;
  }

  @HostBinding('class.disabled')
  @Input()
  set disabled(value: boolean) {
    if (value !== undefined) {
      this.setDisabled(value);
    }
  }

  get disabled(): boolean {
    return this.internalDisabled;
  }

  @Input()
  set value(v: TValue) {
    this.updateValue(v);
  }

  get value(): TValue {
    return this.internalValue;
  }

  @Output() activated = new EventEmitter<MouseEvent | KeyboardEvent>();
  @Output() focusOut = new EventEmitter<FocusEvent>();

  @HostListener('mouseup', ['$event'])
  protected onActivated(event: MouseEvent | KeyboardEvent): void {
    this.activated.emit(event);
  }

  @HostListener('focusout', ['$event'])
  protected onFocusOut(event: FocusEvent): void {
    this.onTouched();
    this.focusOut.emit(event);
  }

  get errors(): ValidationErrors {
    return this.formControl.errors;
  }

  get touched(): boolean {
    return this.formControl.touched;
  }

  get dirty(): boolean {
    return this.formControl.dirty;
  }

  get valid(): boolean {
    return this.formControl.valid;
  }

  get control(): UntypedFormControl {
    return this.formControl;
  }

  constructor() {
    this.isNgForm = this.controlContainer?.formDirective instanceof NgForm;

    // usage inside a template-driven form
    if (this.isNgForm) {
      this.formControl = new UntypedFormControl();
      this.formControl.setValidators(this.validators);
      this.formControl.setAsyncValidators(this.asyncValidators);
    }

    CustomInputComponentAbstract.increment++;
    this.instanceId = CustomInputComponentAbstract.increment;
  }

  ngOnInit(): void {
    // reactive form usage, so get the formControl from ControlContainer instance
    if (!this.formControl) {
      const controlContainerControl = this.controlContainer?.control.get(this.formControlName);
      this.formControl =
        controlContainerControl instanceof UntypedFormControl ? controlContainerControl : new UntypedFormControl();
      // update disabled status on formControl if it was created above
      if (!controlContainerControl) {
        this.setFormControlDisabledState();
        this.updateFormControlValue(this.internalValue);
      }
    }

    const controlVal = this.formControl.value;
    // check against null/undefined as already existing formControl value also could be falsy
    const initialVal = controlVal !== undefined && controlVal !== null ? controlVal : this.value;

    this.formControl.valueChanges
      .pipe(
        startWith(initialVal as string),
        // don't use distinctUntilChanged as value also could have been set via component value setter,
        // in such case distinctUntilChanged still filters against last valueChanges value which is wrong.
        filter(v => v === this.formControl.value)
      )
      .subscribe(value => this.updateValue(value));

    this.substituteNameAndId();
  }

  setDisabledState(disabled: boolean, emitEvent = true): void {
    this.setDisabled(disabled, emitEvent);
  }

  /**
   * Invoked when the model has been changed
   */
  onChange: (_: any) => void = (_: any) => {
    /* intentionally left blank */
  };

  /**
   * Invoked when the model has been touched
   */
  onTouched: () => void = () => {
    /* intentionally left blank */
  };

  /**
   * Invoked when `disabled` property has been changed
   */
  onDisabledChange: (_: any) => void = (_: any) => {
    /* intentionally left blank */
  };

  /**
   * Required in order to avoid calling `super.disabled = ...` from inheriting classes
   * as apparently calling super.properties not possible when compiling to ES5 target.
   * The method also can be used to disable the input suppressing valueChanges emission
   * by setting `emit` param to `false`.
   */
  protected setDisabled(value: boolean, emitEvent = true): void {
    // allow setting an empty value as true
    value = value !== false;

    if (value !== this.internalDisabled) {
      this.internalDisabled = value;
      /*
       * Check for fomControl here as `disabled` maybe invoked before OnInit
       * where fomControl will be instantiated if not present
       * (which happens when not inside any form).
       * That's also the reason for having an own internalDisabled property
       * and not relying on formControl.disabled directly.
       */
      this.setFormControlDisabledState(emitEvent);
      this.onDisabledChange(value);
    }
  }

  protected setFormControlDisabledState(emitEvent = true): void {
    if (this.formControl && this.formControl.disabled !== this.internalDisabled) {
      const opts = { emitEvent };
      this.internalDisabled ? this.formControl.disable(opts) : this.formControl.enable(opts);
    }
  }

  protected substituteNameAndId(): void {
    if (!this.name) {
      if (this.formControlName) {
        this.name = this.formControlName;
        if (this.controlContainer?.control.parent) {
          const parent = this.controlContainer.control.parent;
          this.name =
            Object.keys(parent.controls).find(key => (parent as AbstractControl).get(key) === this.formControl.parent) +
            this.name;
        }
      } else {
        this.name = 'custom-input-' + this.instanceId;
      }
    }

    if (!this.inputId) {
      this.inputId = this.name;
    }
  }

  protected updateValue(value: TValue): void {
    value = sanitizeText(value);
    const hasChanged = value !== this.internalValue;
    this.internalValue = value;
    if (hasChanged && (this.isNgForm || !this.controlContainer)) {
      this.onChange(value);
    }
    this.updateFormControlValue(value);
    this.cdr.markForCheck();
  }

  protected updateFormControlValue(v: TValue, emitEvent = false): void {
    if (this.formControl && this.formControl.value !== v) {
      // don't emit as it's no user-initiated change
      this.formControl.setValue(v, { emitEvent });
    }
  }

  ///////////////
  // OVERRIDES //
  ///////////////

  /**
   * Writes a new item to the element.
   * @param value the value
   */
  writeValue(value: TValue): void {
    if (value) {
      this.internalValue = value;
      if (this.isNgForm) {
        this.formControl.setValue(value);
      }
    }
  }

  /**
   * Registers a callback function that should be called when the control's value changes in the UI.
   * @param fn
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control receives a blur event.
   * @param fn
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnDisabledChange(fn: any): void {
    this.onDisabledChange = fn;
  }
}

// helper to create providers from custom input component
export function makeCustomInputProviders<T = string | object>(type: Type<CustomInputComponentAbstract<T>>): Provider[] {
  return [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => type),
      multi: true
    },
    {
      provide: CustomInputComponentAbstract,
      useExisting: forwardRef(() => type),
      multi: true
    }
  ];
}
